import { useState } from "react";
import { I } from "./primitives";

/**
 * Single-password gate. The hub's DASHBOARD_PASSWORD env var is the secret;
 * we just POST it to /api/v1/auth/login and store the session cookie.
 *
 * Multi-user accounts can layer on later — the LoginScreen component stays
 * the same, the form just gains a username field.
 */
export function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!password) return;
    setSubmitting(true);
    setError(null);
    try {
      await onLogin(password);
    } catch (err) {
      if (err.status === 429) {
        setError("Terlalu banyak percobaan. Coba lagi dalam 1 menit.");
      } else if (err.status === 401) {
        setError("Password salah.");
      } else if (err.status === 503) {
        setError("Hub belum mengkonfigurasi DASHBOARD_PASSWORD.");
      } else {
        setError(err.message || "Login gagal");
      }
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--bg)",
        padding: 16,
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: "100%",
          maxWidth: 380,
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          padding: "28px 28px 24px",
          boxShadow: "0 10px 40px rgba(0,0,0,.18)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background:
                "linear-gradient(135deg, var(--accent), var(--accent-strong, var(--accent)))",
            }}
            aria-hidden="true"
          />
          <div style={{ fontWeight: 600, fontSize: 17 }}>Monibot</div>
        </div>
        <div style={{ fontSize: 13, color: "var(--text-3)", marginBottom: 22 }}>
          Masuk untuk mengakses dashboard monitoring.
        </div>

        <label className="field-label" htmlFor="login-password">
          Password Dashboard
        </label>
        <div className="input" style={{ marginTop: 4 }}>
          <input
            id="login-password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            autoComplete="current-password"
            placeholder="••••••••"
            style={{ fontFamily: "var(--font-mono)" }}
            disabled={submitting}
          />
          <button
            type="button"
            className="btn-icon bare"
            style={{ width: 24, height: 24 }}
            title={showPassword ? "Sembunyikan" : "Tampilkan"}
            onClick={() => setShowPassword(!showPassword)}
            tabIndex={-1}
          >
            {showPassword ? <I.close /> : <I.copy />}
          </button>
        </div>

        {error && (
          <div
            style={{
              marginTop: 12,
              padding: "8px 12px",
              borderRadius: 8,
              background: "var(--crit-soft)",
              border: "1px solid var(--crit)",
              color: "var(--crit)",
              fontSize: 12.5,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <I.alert />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary"
          style={{
            width: "100%",
            marginTop: 20,
            justifyContent: "center",
            opacity: submitting || !password ? 0.6 : 1,
            pointerEvents: submitting || !password ? "none" : "auto",
          }}
          disabled={submitting || !password}
        >
          {submitting ? "Memverifikasi…" : "Masuk"}
        </button>

        <div
          style={{
            marginTop: 18,
            fontSize: 11.5,
            color: "var(--text-4)",
            lineHeight: 1.5,
          }}
        >
          Session cookie aktif selama 30 hari dan disimpan HttpOnly. Untuk
          logout di semua tab: tekan tombol logout di header dashboard.
        </div>
      </form>
    </div>
  );
}
